import React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import Button from '../ui/Button';

interface ErrorFallbackProps {
  error: Error;
  resetError?: () => void;
  showHomeButton?: boolean;
  title?: string;
  message?: string;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  showHomeButton = false,
  title = 'Algo salió mal',
  message = 'Ha ocurrido un error inesperado. Intenta recargar la página.',
}) => {
  const handleReload = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-orange-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-red-200 p-8">
        <div className="flex flex-col items-center text-center">
          <div className="bg-red-100 rounded-full p-4 mb-4">
            <AlertCircle className="h-12 w-12 text-red-600" />
          </div>

          <h1 className="text-2xl font-bold text-stone-900 mb-3">
            {title}
          </h1>

          <p className="text-stone-600 mb-6">
            {message}
          </p>

          {import.meta.env.DEV && (
            <details className="w-full mb-6 text-left">
              <summary className="text-sm font-medium text-stone-700 cursor-pointer hover:text-stone-900 mb-2">
                Detalles técnicos (solo en desarrollo)
              </summary>
              <div className="bg-stone-50 rounded-lg p-4 text-xs font-mono text-stone-800 overflow-auto max-h-40">
                <div className="mb-2">
                  <strong>Error:</strong> {error.message}
                </div>
                {error.stack && (
                  <div>
                    <strong>Stack trace:</strong>
                    <pre className="mt-1 whitespace-pre-wrap break-words">
                      {error.stack}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={handleReload}
              icon={RefreshCw}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Intentar de nuevo
            </Button>

            {showHomeButton && (
              <Button
                onClick={handleGoHome}
                icon={Home}
                variant="outline"
                className="flex-1"
              >
                Volver al inicio
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
